<template>
    <article class="u-file__wrapper">
        <CardResource
            v-if="!error && completed"
            :completed="isCompleted"
            :id="data.id"
            :name="convertedName || name"
            :category="selectedCategory"
            :area="selectedArea"
            :group="selectedGroup"
            :jurisdiction="jurisdiction"
            :url="data.url"
            :showArea="true"
            :showAllMeta="true"
            :isUploadCard="true"
            :tags="tags"
            :failed="data.failedToProcess || false"
            type="document"
            @editCard="editResource"
            v-bind="response"
        >
            <template v-slot:meta>
                <cardMeta v-if="tags && tags.length > 0" label="Tags" :value="tags" :fullWidth="true" />
                <!-- <cardMeta v-if="tags_nouns.length > 0" label="Tags (Nouns)" :value="tags_nouns" :fullWidth="true" /> -->
                <cardMeta v-if="data.url" label="URL" :value="data.url" :fullWidth="true" :docURL="data.url" />
            </template>
        </CardResource>

        <CardUploading
            v-if="!completed || error"
            :name="name"
            :completed="completed"
            :percentage="percentage"
            :error="error"
        />
    </article>
</template>

<script>
import { mapGetters } from 'vuex'
import { toTitleCase } from '@/helpers'

export default {
    inheritAttrs: false,

    props: {
        name: { type: String, required: true },
        completed: { type: Boolean, default: false },
        percentage: { type: [String, Number], default: 0 },
        error: { type: String, default: null },
        data: { type: Object, default: () => {} },
        response: { type: Object }
    },

    data () {
        return {
            selectedArea: [],
            selectedCategory: [],
            selectedGroup: [],
            convertedName: null,
            jurisdiction: null,
            tags: null,
            tags_nouns: null,
            newFile: [],
            editing: false,
            setData: null,
            loading: false,
            uploaded: false,
            confirmDelete: false,
            deleting: false,
            deleted: false,
            timer: 0,
            updateError: null
        }
    },

    filters: {
        // Converts the file name to title case
        // TODO: CONVERT TO HELPER FUNCTION
        fileToTitle (val) {
            const convertedVal = val
                .replace(/\.\w+$/, '') // remove file extension
                .replace(/([a-z])([A-Z])/g, (allMatches, firstMatch, secondMatch) => `${firstMatch} ${secondMatch}`) // split pascal case
                .replace(/([^a-zA-Z0-9()]+)(.)/g, (allMatches, firstMatch, secondMatch) => {
                    return `${(firstMatch ? ' ' : '')} ${secondMatch.toUpperCase()}`
                }) // remove all special characters
                .replace(/\s\s+/g, ' ') // remove additional spaces

            return convertedVal.charAt(0).toUpperCase() + convertedVal.slice(1)
        },

        titleCase (val) { return toTitleCase(val) },
    },

    mounted () {
        this.convertedName = this.convertFileToTitleCase(this.name)
    },

    computed: {
        ...mapGetters('Resources', ['formOptionsAreas', 'formOptionsGroups', 'formOptionsCategories']),

        isCompleted () {
            // We assign the data received from ajax (as a prop), and assign to the components state,
            // to use with v-model (rather than try add reactivity to the props themselves)
            if (this.completed) this.setSelectedValues()
            return this.completed
        },

        buttonLabel () {
            if (this.loading) return 'Updating Resource'
            if (this.uploaded) return 'Successfully updated'
            return 'Save Changes'
        },
    },

    methods: {
        convertFileToTitleCase (val) {
            const convertedVal = val
                .replace(/\.\w+$/, '') // remove file extension
                .replace(/([a-z])([A-Z])/g, (allMatches, firstMatch, secondMatch) => `${firstMatch} ${secondMatch}`) // split pascal case
                .replace(/([^a-zA-Z0-9()]+)(.)/g, (allMatches, firstMatch, secondMatch) => {
                    return `${(firstMatch ? ' ' : '')} ${secondMatch.toUpperCase()}`
                }) // remove all special characters
                .replace(/\s\s+/g, ' ') // remove additional spaces

            return convertedVal.charAt(0).toUpperCase() + convertedVal.slice(1)
        },

        setSelectedValues() {
            const { area, category, group, jurisdiction, tags, tags_nouns } = this.data
            const states = ['vic', 'nsw', 'act', 'nt', 'sa', 'wa', 'tas', 'qld']

            const juris = states.includes(jurisdiction.toLowerCase())
                ? jurisdiction.toUpperCase()
                : jurisdiction

            const hasTags = tags && tags.length > 0
            const hasTagNouns = tags_nouns && tags_nouns.length > 0

            this.selectedArea = area
            this.selectedCategory = category.toLowerCase()
            this.selectedGroup = group.toLowerCase()
            this.jurisdiction = juris
            this.tags = hasTags && tags.reduce((acc, tag) => `${acc}, ${tag}`) || null
            this.tags_nouns = hasTagNouns && tags_nouns.reduce((acc, tag) => `${acc}, ${tag}`) || null
        },

        editResource () {
            this.$emit('lockTabs')
            this.editing = true
            this.updateError = null

            // We take a copy of the v-model data, so if the user cancels the editing, we know what
            // data to re-apply to the resource
            this.setData = {
                selectedArea: this.selectedArea,
                selectedCategory: this.selectedCategory,
                selectedGroup: this.selectedGroup,
                options: this.options,
                convertedName: this.convertedName,
                jurisdiction: this.jurisdiction,
                tags: this.tags
            }
        },

        cancelEditingWithoutSaving () {
            // ...if the user cancels, reset all the data with the data we just stored in the previous func
            Object.keys(this.setData).forEach(key => {
                this[key] = this.setData[key]
            })

            this.editing = false
            this.$emit('unlockTabs')
        },

        deleteResource () {
            this.confirmDelete = false
            this.deleting = true
            this.updateError = null

            this.axios.delete('/resource', { id: this.data.id })
                .then(() => {
                    this.deleted = true
                    this.timer = 3 // we'll let the user know that the panel will close in X seconds

                    setInterval(() => {
                        if (this.timer > 0) this.timer -= 1 // increment the timer by 1 until it's back to 0
                    }, 1000);

                    setTimeout(() => {
                        this.deleted = false
                        this.editing = false
                        this.$emit('unlockTabs')
                    }, 3000); // time to close the panel...

                    // we delay this slightly to ensure the close panel animation has animated out
                    setTimeout(() => this.$emit('removeResource', this.id), 3300)
                })
                .catch(err => this.handleError(err))
                .finally(() => { this.deleting = false })
        },

        updateDetails () {
            this.loading = true
            this.updateError = null

            const data = {
                selectedArea: this.selectedArea,
                selectedCategory: this.selectedCategory,
                selectedGroup: this.selectedGroup,
                convertedName: this.convertedName,
                jurisdiction: this.jurisdiction,
                tags: this.tags,
                newFile: this.newFile
            }

            // submits the new data
            this.axios.put('/resources', data)
                .then(() => {
                    this.uploaded = true
                    this.setData = null // new data has been saved, we no longer need the old data...

                    setTimeout(() => {
                        this.uploaded = false
                        this.editing = false
                        this.$emit('unlockTabs')
                    }, 1500);
                })
                .catch(err => this.handleError(err))
                .finally(() => { this.loading = false })
        },

        handleError (err) {
            this.updateError = err.message || err
        }
    }
}
</script>

